import { ParsedCSVData, ValidationError } from '@/types/upload';
import {
  PointItemNameSchema,
  PointPlanNameSchema,
  RequestPlatformSchema,
} from '@/types/api.zod';
import { type CsvRow, toCsvRow } from '@/utils/csvRow';

/**
 * eコイン購入プラン CSV のクライアント側バリデーション。
 *
 * S3 へ presigned URL でアップロードする前に、Book / Menu / Coupon と同様に
 * 全ルールをクライアントで検証してプレビュー可否を判定する。サーバー（ProcessUpload Lambda /
 * PointUploadService）でも同一ルールを再検証する（多層防御）。
 *
 * Coupon のフラット形式と違い **1 CSV = 1 プラン**の階層形式で、行の役割は値を入れる列で区別する:
 *   - 3 行目（プラン行）: plan_name 非空 & platform 空。plan_name / start_at / end_at / note を記入
 *   - 4 行目以降（明細行）: plan_name 空 & platform 非空。platform / item_name / display_name /
 *     price / bonus_ecoin / total_ecoin / note を記入
 * 明細の記載順がそのまま表示順になる（プラットフォームごとに上から採番）。
 *
 * 仕様の正本: docs/features/point.md「CSV フォーマット」「CSV バリデーションルール」。
 * platform / plan_name / item_name の書式は codegen の Zod スキーマ（単一の出所）から導出する。
 *
 * 権威はサーバー（point_service.py）側にあり、ここはアップロード前の UX 向け事前検証。
 * 日時 / 数値範囲 / 行種別判定等のルールはバックエンドと手動ミラーのため、
 * 仕様変更時は docs/features/point.md を基点に point_service.py と本ファイルの両方を更新すること。
 */

const COLUMNS = [
  'plan_name',
  'start_at',
  'end_at',
  'platform',
  'item_name',
  'display_name',
  'price',
  'bonus_ecoin',
  'total_ecoin',
  'note',
] as const;

// 列名をキーに持つ 1 行分の型（値は常に string）。詳細は utils/csvRow.ts。
type PointColumn = (typeof COLUMNS)[number];
type PointRow = CsvRow<typeof COLUMNS>;

// 行種別の判定: プラン行 = plan_name 非空 & platform 空 / 明細行 = plan_name 空 & platform 非空。
// validatePointCsv（行種別の分岐）と countPointItemsByPlatform（明細行の抽出）の**唯一の判定**として
// ここに集約する（2 箇所に重複させると片方だけ変わって乖離するため。MR !544 レビュー指摘）。
const isPlanRow = (d: PointRow): boolean => d.plan_name !== '' && d.platform === '';
const isItemRow = (d: PointRow): boolean => d.plan_name === '' && d.platform !== '';

// プラン行にのみ記入できる列（明細行にあるとエラー）。note は共通列。
const PLAN_ONLY_COLUMNS: readonly PointColumn[] = ['start_at', 'end_at'];
// 明細行にのみ記入できる列（プラン行にあるとエラー）。
const ITEM_ONLY_COLUMNS: readonly PointColumn[] = [
  'item_name',
  'display_name',
  'price',
  'bonus_ecoin',
  'total_ecoin',
];

const PLATFORMS: readonly string[] = RequestPlatformSchema.options;

const INTEGER_RE = /^-?\d+$/;
// CSV 日時（`YYYY-MM-DD HH:mm`・JST）。commons の CsvDateTime（codegen）と同じ範囲を受理する:
// 年月日と時は `/` `-` 区切り・ゼロパディング省略可だが、**分と秒は 2 桁固定**。
// 時刻の省略とタイムゾーン表記は認めない。
// ⚠ point_service.py の _CSV_DATETIME_PATTERN と完全に一致させること。緩いとサーバーだけが
// 弾く形になり、事前検証を通ったのに全行エラーになる。
const CSV_DATETIME_RE = /^(\d{4})[/-](\d{1,2})[/-](\d{1,2}) (\d{1,2}):(\d{2})(?::(\d{2}))?$/;
const DATETIME_FORMAT_HINT = '（例: 2026-04-01 00:00・JST。日付のみ / タイムゾーン付きは不可）';

const DISPLAY_NAME_MAX_LENGTH = 50; // schemas の PointPlanUploadItem.display_name maxLength と一致
const NOTE_MAX_LENGTH = 200; // schemas の note maxLength と一致
const MAX_ITEM_ROWS = 300; // schemas の PointPlanUpload.items maxItems と一致
// 金額系（price / bonus_ecoin / total_ecoin）の上限。桁の打ち間違い（2000 → 20000000）を
// 止めるサニティ上限。schemas の maximum・point_service.py の _AMOUNT_MAX と一致させること。
const AMOUNT_MAX = 1000000;

const err = (row: number, column: string, value: string, error: string): ValidationError => ({
  row,
  column,
  value,
  error,
});

// 存在しない日付（例: 2026-02-30）を UTC ロールオーバーの有無で弾く（couponCsvValidation と同一方式）
const isRealDate = (year: number, month: number, day: number): boolean => {
  const probe = new Date(Date.UTC(year, month - 1, day));
  return (
    probe.getUTCFullYear() === year &&
    probe.getUTCMonth() === month - 1 &&
    probe.getUTCDate() === day
  );
};

type DateTimeParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

/** CSV 日時を分解する。書式不正・時刻範囲外・存在しない日付は null。 */
const parseDateTime = (value: string): DateTimeParts | null => {
  const matched = CSV_DATETIME_RE.exec(value.trim());
  if (!matched) return null;
  const [, y, mo, d, h, mi, sec] = matched;
  const parts: DateTimeParts = {
    year: Number(y),
    month: Number(mo),
    day: Number(d),
    hour: Number(h),
    minute: Number(mi),
    second: Number(sec ?? 0),
  };
  if (parts.hour > 23 || parts.minute > 59 || parts.second > 59) return null;
  if (!isRealDate(parts.year, parts.month, parts.day)) return null;
  return parts;
};

const isValidDateTime = (value: string): boolean => parseDateTime(value) !== null;

// CSV のタイムゾーン無し日時は JST（UTC+9）とみなす（サーバ DateTimeUtils と一致）。
const JST_OFFSET_MS = 9 * 60 * 60 * 1000;

const toEpoch = (p: DateTimeParts): number =>
  Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - JST_OFFSET_MS;

/** 整数列をパースする。空欄は必須エラー、非整数は形式エラー。 */
const parseInteger = (
  rowNum: number,
  column: PointColumn,
  value: string,
  errors: ValidationError[]
): number | null => {
  if (!value) {
    errors.push(err(rowNum, column, value, `${column} は必須です`));
    return null;
  }
  if (!INTEGER_RE.test(value)) {
    errors.push(err(rowNum, column, value, `${column} は整数で指定してください`));
    return null;
  }
  return Number(value);
};

/**
 * 行を 10 列に正規化する。正規化できない（余分な非空列がある）場合は null。
 * Excel 保存時に付く末尾の余分な空カラムは無視し、行末の列省略は toCsvRow が空文字で補う。
 */
const normalizeRow = (row: readonly string[]): string[] | null => {
  const cells = row.map((c) => c.trim());
  while (cells.length > COLUMNS.length) {
    const last = cells[cells.length - 1];
    if (last !== '') return null;
    cells.pop();
  }
  return cells;
};

const validatePlanRow = (rowNum: number, d: PointRow, errors: ValidationError[]): void => {
  if (!PointPlanNameSchema.safeParse(d.plan_name).success) {
    errors.push(
      err(
        rowNum,
        'plan_name',
        d.plan_name,
        'plan_name は半角英数・ハイフン（-）・アンダースコア（_）の 3〜64 桁で指定してください'
      )
    );
  }

  if (!d.start_at) {
    errors.push(err(rowNum, 'start_at', '', 'start_at は必須です'));
  } else if (!isValidDateTime(d.start_at)) {
    errors.push(
      err(rowNum, 'start_at', d.start_at, `start_at の日時形式が不正です${DATETIME_FORMAT_HINT}`)
    );
  }

  if (d.end_at && !isValidDateTime(d.end_at)) {
    errors.push(
      err(rowNum, 'end_at', d.end_at, `end_at の日時形式が不正です${DATETIME_FORMAT_HINT}`)
    );
  }

  // start_at < end_at（両方妥当な場合のみ。同時刻も不可）
  const start = parseDateTime(d.start_at);
  const end = parseDateTime(d.end_at);
  if (start !== null && end !== null && toEpoch(end) <= toEpoch(start)) {
    errors.push(
      err(rowNum, 'end_at', d.end_at, 'end_at は start_at より後の日時で指定してください')
    );
  }

  validateNote(rowNum, d, errors);

  ITEM_ONLY_COLUMNS.forEach((col) => {
    if (d[col]) {
      errors.push(err(rowNum, col, d[col], `プラン行に ${col} は指定できません`));
    }
  });
};

const validateItemRow = (rowNum: number, d: PointRow, errors: ValidationError[]): void => {
  if (!PLATFORMS.includes(d.platform)) {
    errors.push(
      err(
        rowNum,
        'platform',
        d.platform,
        `platform は ${[...PLATFORMS].sort().join(' / ')} のいずれかです（小文字）`
      )
    );
  }

  if (!d.item_name) {
    errors.push(err(rowNum, 'item_name', '', 'item_name は必須です'));
  } else if (!PointItemNameSchema.safeParse(d.item_name).success) {
    errors.push(
      err(
        rowNum,
        'item_name',
        d.item_name,
        'item_name は半角英数・ピリオド（.）・ハイフン（-）・アンダースコア（_）の 100 桁以内です'
      )
    );
  }

  if (!d.display_name) {
    errors.push(err(rowNum, 'display_name', '', 'display_name は必須です'));
  } else if (d.display_name.length > DISPLAY_NAME_MAX_LENGTH) {
    errors.push(
      err(
        rowNum,
        'display_name',
        d.display_name,
        `display_name は ${DISPLAY_NAME_MAX_LENGTH} 文字以内で指定してください`
      )
    );
  }

  // 上限は桁誤り検出のためのサニティ値（AMOUNT_MAX。schemas の maximum と一致）
  const price = parseInteger(rowNum, 'price', d.price, errors);
  if (price !== null && (price < 1 || price > AMOUNT_MAX)) {
    errors.push(err(rowNum, 'price', d.price, `price は 1〜${AMOUNT_MAX} の整数で指定してください`));
  }

  const bonus = parseInteger(rowNum, 'bonus_ecoin', d.bonus_ecoin, errors);
  if (bonus !== null && (bonus < 0 || bonus > AMOUNT_MAX)) {
    errors.push(
      err(
        rowNum,
        'bonus_ecoin',
        d.bonus_ecoin,
        `bonus_ecoin は 0〜${AMOUNT_MAX} の整数で指定してください`
      )
    );
  }

  const total = parseInteger(rowNum, 'total_ecoin', d.total_ecoin, errors);
  if (total !== null && (total < 1 || total > AMOUNT_MAX)) {
    errors.push(
      err(
        rowNum,
        'total_ecoin',
        d.total_ecoin,
        `total_ecoin は 1〜${AMOUNT_MAX} の整数で指定してください`
      )
    );
  }

  // base_ecoin（= total - bonus）が負にならないこと
  if (bonus !== null && total !== null && total < bonus) {
    errors.push(
      err(
        rowNum,
        'total_ecoin',
        d.total_ecoin,
        'total_ecoin は bonus_ecoin 以上で指定してください（基本付与分が負になります）'
      )
    );
  }

  validateNote(rowNum, d, errors);

  PLAN_ONLY_COLUMNS.forEach((col) => {
    if (d[col]) {
      errors.push(err(rowNum, col, d[col], `明細行に ${col} は指定できません`));
    }
  });
};

const validateNote = (rowNum: number, d: PointRow, errors: ValidationError[]): void => {
  if (d.note && d.note.length > NOTE_MAX_LENGTH) {
    errors.push(err(rowNum, 'note', d.note, `note は ${NOTE_MAX_LENGTH} 文字以内で指定してください`));
  }
};

export const validatePointCsv = (data: ParsedCSVData): ValidationError[] => {
  const errors: ValidationError[] = [];

  // ヘッダー（順序込み完全一致）
  const headerMatches =
    data.headers.length === COLUMNS.length && COLUMNS.every((c, i) => data.headers[i] === c);
  if (!headerMatches) {
    return [
      err(
        1,
        'ヘッダー',
        data.headers.join(','),
        `CSVヘッダーが不正です。期待: ${COLUMNS.join(',')}`
      ),
    ];
  }

  // 説明行（2 行目）の欠落検知: 誤って削除するとプラン行が説明行として読み飛ばされるため、
  // 2 行目（parseCSV の displayHeaders）の plan_name セルがプラン名の形式に一致する場合はエラー。
  const descriptionPlanName = (data.displayHeaders[0] ?? '').trim();
  if (PointPlanNameSchema.safeParse(descriptionPlanName).success) {
    return [
      err(
        2,
        'plan_name',
        descriptionPlanName,
        '説明行（2 行目）がありません（2 行目がプラン行になっています）'
      ),
    ];
  }

  if (data.rows.length === 0) {
    return [
      err(
        1,
        'データ',
        '',
        'データ行がありません（1=カラム名 / 2=説明 / 3=プラン行 / 4 行目以降=明細行）'
      ),
    ];
  }

  // parseCSV が 2 行目（説明行）をスキップ済みのため data.rows[0] は実ファイル 3 行目に相当する。
  const planRows: number[] = [];
  const itemRows: number[] = [];
  const seenItemNames = new Map<string, number>(); // `platform#item_name` → 初出行

  data.rows.forEach((row, idx) => {
    const rowNum = idx + 3;

    const normalized = normalizeRow(row);
    if (normalized === null) {
      errors.push(
        err(
          rowNum,
          '列数',
          row.join(','),
          `列数が不正です（${row.length} 列、期待 ${COLUMNS.length} 列）`
        )
      );
      return;
    }
    if (normalized.every((c) => c === '')) return; // 全列空は空行として無視

    const d = toCsvRow(COLUMNS, normalized);

    if (isPlanRow(d)) {
      planRows.push(rowNum);
      validatePlanRow(rowNum, d, errors);
    } else if (isItemRow(d)) {
      itemRows.push(rowNum);
      validateItemRow(rowNum, d, errors);

      // 同一プラットフォーム内の item_name 重複
      if (d.item_name) {
        const key = `${d.platform}#${d.item_name}`;
        const firstRow = seenItemNames.get(key);
        if (firstRow !== undefined) {
          errors.push(
            err(
              rowNum,
              'item_name',
              d.item_name,
              `platform=${d.platform} 内で item_name が重複しています: '${d.item_name}'（${firstRow} 行目と同一）`
            )
          );
        } else {
          seenItemNames.set(key, rowNum);
        }
      }
    } else {
      errors.push(
        err(
          rowNum,
          '行種別',
          row.join(','),
          '行種別が判定できません（プラン行は plan_name のみ、明細行は platform のみを記入）'
        )
      );
    }
  });

  // 構造検証（行跨ぎ）
  if (planRows.length === 0) {
    errors.push(err(1, 'プラン行', '', 'プラン行がありません（CSV につき 1 行必須）'));
  } else if (planRows.length > 1) {
    const secondRow = planRows[1];
    if (secondRow !== undefined) {
      errors.push(
        err(secondRow, 'プラン行', '', `プラン行は 1 行のみ可（検出行: ${planRows.join(', ')}）`)
      );
    }
  }
  if (itemRows.length === 0) {
    errors.push(err(1, '明細行', '', '明細行がありません（最低 1 商品必須）'));
  } else {
    // 上限超過は 301 件目の実ファイル行番号で報告する（point_service.py と同じ位置）
    const firstOverflowRow = itemRows[MAX_ITEM_ROWS];
    if (firstOverflowRow !== undefined) {
      errors.push(
        err(
          firstOverflowRow,
          '明細行',
          '',
          `明細行が上限を超えています（${itemRows.length} 行、上限 ${MAX_ITEM_ROWS} 行）`
        )
      );
    }
  }

  return errors;
};

/** プラットフォーム別の明細件数（RequestPlatform の定義順。商品が無いものは count=0 で必ず含む）。 */
export type PointPlatformItemCount = { platform: string; count: number };

/**
 * プレビュー用に、プラットフォームごとの明細行数を数える。
 *
 * **0 件のプラットフォームも必ず返す**のが本関数の目的。記載のないプラットフォームは
 * そのプランの適用期間中「販売停止」になる（旧プランは継続しない）ため、反映前に
 * 気づけるようにプレビューへ 0 件を明示する（docs/features/point.md ADR-008）。
 * バリデーションではエラーにしない（意図的な販売停止を表現できるようにするため）。
 *
 * 行種別の判定は validatePointCsv と共通の isItemRow を使う（判定条件の唯一の出所）。
 * 検証を通過したデータに対して呼ぶ前提のため、列数エラー・未知の platform 値は考慮しない
 * （未知の値は PLATFORMS に無いため結果に現れない）。
 */
export const countPointItemsByPlatform = (data: ParsedCSVData): PointPlatformItemCount[] => {
  const itemPlatforms = data.rows
    .map((row) => toCsvRow(COLUMNS, row))
    .filter(isItemRow) // プラン行・空行・判定不能行は数えない
    .map((d) => d.platform);

  return PLATFORMS.map((platform) => ({
    platform,
    count: itemPlatforms.filter((p) => p === platform).length,
  }));
};
